import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

const SIZE_HEIGHT = { md: 'h-7', lg: 'h-8' } as const;

function InputGroup({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<'div'> & {
  /** Field height — `md` = 28px, `lg` = 32px */
  size?: 'md' | 'lg';
}) {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      role="group"
      className={cn(
        'group/input-group relative flex w-full items-center rounded-md border border-grey-75 bg-grey-0 shadow-none transition-[color,box-shadow,border-color] outline-none',
        SIZE_HEIGHT[size],
        'min-w-0 has-[>textarea]:h-auto',
        'has-[>[data-align=inline-start]]:[&>input]:pl-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-1',
        'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3',
        // Match Input: darker border + grey hover, brand focus (stays brand on selected hover).
        'hover:border-grey-200 hover:shadow-secondary',
        'focus-within:border-primary focus-within:shadow-primary focus-within:hover:border-primary focus-within:hover:shadow-primary',
        // Error: red border at rest; red border + glow on hover / focus (same as Input).
        'has-[[data-slot=input-group-control][aria-invalid=true]]:border-destructive',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:hover:border-destructive',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:hover:shadow-destructive',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:border-destructive',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:shadow-destructive',
        'has-[[data-slot=input-group-control][aria-invalid=true]]:focus-within:hover:shadow-destructive',
        'has-disabled:cursor-not-allowed has-disabled:border-transparent has-disabled:bg-grey-25 has-disabled:text-grey-400 has-disabled:hover:border-transparent has-disabled:hover:shadow-none',
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  // No vertical padding — parent InputGroup owns h-7/h-8; py-* was nudging multi/single past 28px.
  "flex h-auto cursor-text items-center justify-center gap-1 text-sm font-medium text-grey-500 select-none group-data-[disabled=true]/input-group:opacity-50 [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-2 has-[>button]:ml-[-0.25rem]',
        'inline-end': 'order-last pr-1 has-[>button]:mr-[-0.15rem]',
        'block-start':
          'order-first w-full justify-start px-2 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end': 'order-last w-full justify-start px-2 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
);

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  const focusInput = (e: React.SyntheticEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    e.currentTarget.parentElement?.querySelector('input')?.focus();
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- mirrors shadcn InputGroup; click focuses input, keyboard users tab to the field
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={focusInput}
      {...props}
    />
  );
}

type InputGroupButtonSize = 'xs' | 'sm' | 'icon-xs' | 'icon-sm';

function InputGroupButton({
  className,
  type = 'button',
  variant = 'tertiary',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'variant'> & {
  variant?: React.ComponentProps<typeof Button>['variant'];
  size?: InputGroupButtonSize;
}) {
  const iconOnly = size === 'icon-xs' || size === 'icon-sm';
  const buttonSize = size === 'icon-sm' || size === 'sm' ? 'md' : 'sm';

  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      size={buttonSize}
      iconOnly={iconOnly}
      className={cn('shadow-none focus-visible:shadow-none', className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      data-slot="input-group-control"
      className={cn(
        'h-full min-w-0 flex-1 rounded-none border-0 bg-transparent px-2 py-1 text-sm font-medium text-grey-800 shadow-none ring-0 outline-none',
        'placeholder:font-normal placeholder:text-grey-300',
        // Number spinner: a left margin on the native up/down arrows opens a gap from the digit (they're
        // inline content, so padding-right can't).
        '[&::-webkit-inner-spin-button]:ml-1.5',
        'focus:shadow-none focus-visible:shadow-none focus-visible:ring-0',
        'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-400',
        className,
      )}
      {...props}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText };
